import moment from 'moment';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';

export type CategoryFilterValue = number | typeof UNCATEGORIZED_VALUE | null;

type UseSpaEntityFilterParams = {
  categoryId: CategoryFilterValue;
  arrivalDate: string | undefined;
  fromDate: string | undefined;
  guests?: number;
};

function parseCategoryValue(value: string | null | undefined): CategoryFilterValue {
  if (!value) return null;
  if (value === UNCATEGORIZED_VALUE) return UNCATEGORIZED_VALUE;
  const num = Number(value);
  return isNaN(num) ? null : num;
}

export function useSpaEntityFilter({
  categoryId,
  arrivalDate,
  fromDate,
  guests: guestsFallback,
}: UseSpaEntityFilterParams) {
  const router = useRouter();

  const [categoryFilterValue, setCategoryFilterValue] = useState<CategoryFilterValue>(categoryId);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    categoryId ? String(categoryId) : null
  );
  const [appliedDate, setAppliedDate] = useState(() => arrivalDate || fromDate || moment().format());

  useEffect(() => {
    if (arrivalDate) {
      setAppliedDate(arrivalDate);
    }
  }, [arrivalDate]);

  const appliedDateString = appliedDate
    ? moment(appliedDate).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');

  const handleDirectCategoryChange = (value: string) => {
    setCategoryFilterValue(parseCategoryValue(value));
    setSelectedCategory(value || null);

    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, category: value || undefined },
      },
      undefined,
      { shallow: true }
    );
  };

  const categoryFilterId = typeof categoryFilterValue === 'number' ? categoryFilterValue : null;

  const guests = router.query.guests ? Number(router.query.guests) : guestsFallback;

  return {
    categoryFilterId,
    categoryFilterValue,
    appliedDateString,
    selectedCategory,
    handleDirectCategoryChange,
    guests,
  };
}
